// Scene + cue hooks for the producer ADR Script Book.
//
// `cues.ts` in this folder is owned by the actor surface (CueListResponse).
// This module exposes the producer-side editing surface — full cue rows, edit
// mutations, bulk operations, export trigger.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import type { CueRow, CueType, SceneSummary } from './producer-types'

export const scenesKey = (episodeId: string) => ['scenes', episodeId] as const
export const cuesKey = (episodeId: string) => ['producer-cues', episodeId] as const

/** Scene headers for an episode. */
export function useScenes(episodeId: string | undefined) {
  return useQuery({
    queryKey: episodeId ? scenesKey(episodeId) : ['scenes', '__none__'],
    enabled: Boolean(episodeId),
    queryFn: () => api<SceneSummary[]>(`/api/v1/episodes/${episodeId}/scenes`),
  })
}

/** Full cue list for an episode (producer view, all characters). */
export function useEpisodeCues(episodeId: string | undefined) {
  return useQuery({
    queryKey: episodeId ? cuesKey(episodeId) : ['producer-cues', '__none__'],
    enabled: Boolean(episodeId),
    queryFn: () => api<CueRow[]>(`/api/v1/episodes/${episodeId}/cues`),
  })
}

export interface UpdateCueInput {
  cue_id: string
  in_sec?: number
  out_sec?: number
  line_text?: string | null
  effort_descriptor?: string | null
  cue_type?: CueType
  character_id?: string | null
}

/** Inline cue edit (line text, in/out TC, type, character). */
export function useUpdateCue(episodeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ cue_id, ...body }: UpdateCueInput) =>
      api<CueRow>(`/api/v1/cues/${cue_id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onMutate: async (input) => {
      const key = cuesKey(episodeId)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<CueRow[]>(key)
      if (prev) {
        qc.setQueryData<CueRow[]>(
          key,
          prev.map((row) => (row.id === input.cue_id ? { ...row, ...input } : row)),
        )
      }
      return { prev }
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(cuesKey(episodeId), ctx.prev)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: cuesKey(episodeId) })
    },
  })
}

export interface SplitCueInput {
  cue_id: string
  /** Seconds at which to split. Must lie strictly between in_sec and out_sec. */
  split_at_sec: number
}

export function useSplitCue(episodeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SplitCueInput) =>
      api<CueRow[]>(`/api/v1/cues/${input.cue_id}/split`, {
        method: 'POST',
        body: JSON.stringify({ split_at_sec: input.split_at_sec }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cuesKey(episodeId) })
    },
  })
}

export function useMergeCues(episodeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { cue_ids: string[] }) =>
      api<CueRow>('/api/v1/cues/merge', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cuesKey(episodeId) })
    },
  })
}

export function useBulkDeleteCues(episodeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { cue_ids: string[] }) =>
      api<{ deleted: number }>('/api/v1/cues/delete', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cuesKey(episodeId) })
    },
  })
}

export function useBulkReassignCharacter(episodeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { cue_ids: string[]; character_id: string }) =>
      api<{ updated: number }>('/api/v1/cues/reassign-character', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cuesKey(episodeId) })
    },
  })
}

/** Build the export URL the user clicks. Server signs and streams. */
export function buildScriptBookExportUrl(
  projectId: string,
  format: 'pdf' | 'csv' | 'json',
): string {
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.ohear.ai'
  return `${apiBase}/api/v1/projects/${projectId}/script-book.${format}`
}
